// Several cards in one answer (five launches), one at a time, without losing
// track: a header that stays pinned at the top while the long card scrolls
// under it says what the answer holds ("5 studies: 3 need your OK"), which
// one is in view ("Study 2 of 5"), where each stands (a numbered pill per
// card, coloured by status, click to jump) and moves back and forth (the
// arrows). After acting on a card the focus stays on it, so its receipt
// (launching, then live) is what the person sees, with Next to the next one
// that needs them. The full list opens from the header. One card shows as
// it always did.
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FONT, FIG_INK, FIG_MUTED, HAIRLINE, PRIMARY } from "../../design";
import { TONE } from "./CardStyle";
import { cleanTitle } from "./TaskCard";
import { toDisplayBrand } from "../../utils/displayBrand";

const NEEDS = new Set(["proposed", "waiting_approval"]);
const WORD = {
  proposed: "Needs your OK", waiting_approval: "Needs your OK", queued: "Running", running: "Working",
  paused: "Paused", done: "Done", failed: "Did not go through", stopped: "Not launched",
};
const toneOf = (s) => (NEEDS.has(s) ? "blue" : s === "done" || s === "queued" || s === "running" ? "green" : s === "failed" ? "red" : "grey");
const wordOf = (x) => x.word || WORD[x.status] || x.status;
const nameOf = (x) => toDisplayBrand(cleanTitle(x.title || ""));

const Wrap = styled.div`
  font-family: ${FONT};
  margin: 8px 0 4px;
  border: 1px solid ${HAIRLINE};
  border-radius: 14px;
  background: #fafafb;
  padding: 0 12px 6px;
`;

// Pinned while the card scrolls under it.
const Nav = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  margin: 0 -12px 6px;
  padding: 10px 12px 8px;
  background: #fafafb;
  border-bottom: 1px solid ${HAIRLINE};
  border-radius: 14px 14px 0 0;
  /* The strip above it (the list's top padding) is masked, so nothing peeks out over the pinned header. */
  box-shadow: 0 -24px 0 0 #fff, 0 6px 10px -10px rgba(44, 41, 38, 0.35);
  .sum { font-size: 11.5px; color: ${FIG_MUTED}; margin-bottom: 6px; }
  .sum b { color: ${FIG_INK}; font-weight: 700; }
  .row { display: flex; align-items: center; gap: 8px; }
  .now { flex: 1; min-width: 0; display: flex; align-items: baseline; gap: 8px; }
  .now .k { flex: none; font-size: 12px; font-weight: 700; color: ${FIG_INK}; }
  .now .t { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; font-weight: 600; color: ${FIG_INK}; }
  .now .s { flex: none; font-size: 11px; }
  .pills { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 8px; align-items: center; }
  .all { margin-left: auto; font-family: ${FONT}; font-size: 11.5px; font-weight: 600; color: ${PRIMARY}; background: none; border: 0; cursor: pointer; padding: 2px 4px; }
  .all:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; border-radius: 4px; }
`;

const Arrow = styled.button`
  flex: none;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: ${FONT}; font-size: 15px; line-height: 1; color: ${FIG_INK};
  background: #fff; border: 1px solid #dcdad8; border-radius: 8px; cursor: pointer;
  &:hover:not(:disabled) { border-color: ${PRIMARY}; color: ${PRIMARY}; }
  &:disabled { opacity: 0.4; cursor: default; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

const Pill = styled.button`
  min-width: 26px; height: 24px; padding: 0 7px;
  font-family: ${FONT}; font-size: 11.5px; font-weight: 700;
  color: ${(p) => TONE[p.$tone].fg};
  background: ${(p) => TONE[p.$tone].bg};
  border: 2px solid ${(p) => (p.$current ? TONE[p.$tone].fg : "transparent")};
  border-radius: 999px;
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

const List = styled.ol`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  li button {
    width: 100%; display: flex; align-items: center; gap: 10px; text-align: left;
    font-family: ${FONT}; font-size: 12.5px; color: ${FIG_INK};
    background: transparent; border: 1px solid transparent; border-radius: 9px; padding: 5px 8px; cursor: pointer;
    &:hover { background: #fff; }
    &[aria-current="step"] { background: #fff; border-color: #c9d1f7; font-weight: 600; }
    &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 1px; }
  }
  .t { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .s { flex: none; font-size: 11px; }
`;

const NextBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin: 8px 0 6px; padding: 8px 10px; border-radius: 10px; background: #EEF0FC;
  font-size: 12.5px; color: ${FIG_INK};
  span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  button {
    flex: none; font-family: ${FONT}; font-size: 12.5px; font-weight: 600; color: #fff; background: ${PRIMARY};
    border: 0; border-radius: 8px; padding: 6px 12px; cursor: pointer;
    &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
  }
`;

/** "5 studies: 3 need your OK, 1 launching, 1 not launched" */
function summary(list, noun) {
  const counts = {};
  for (const x of list) {
    const w = NEEDS.has(x.status) ? "need your OK" : wordOf(x).toLowerCase();
    counts[w] = (counts[w] || 0) + 1;
  }
  const order = ["need your OK", ...Object.keys(counts).filter((k) => k !== "need your OK")];
  return { head: `${list.length} ${noun}`, rest: order.filter((k) => counts[k]).map((k) => `${counts[k]} ${k}`).join(", ") };
}

/**
 * items: the cards of one answer, in order: [{ id, status, title, word, node }].
 * node is the card itself; only the card in focus is shown.
 */
export default function CardSteps({ items, noun }) {
  const list = (items || []).filter(Boolean);
  const statuses = list.map((x) => x.status).join(",");
  const firstOpen = useMemo(() => list.findIndex((x) => NEEDS.has(x.status)), [statuses]); // eslint-disable-line react-hooks/exhaustive-deps
  // The card in focus is chosen by the person (a pill, an arrow, Next), never
  // moved on its own: it starts on the first card that needs them.
  const [pick, setPick] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { if (pick == null && list.length > 1) setPick(firstOpen > -1 ? firstOpen : list.length - 1); }, [pick, list.length, firstOpen]);
  if (list.length <= 1) return <>{list.map((x) => <React.Fragment key={x.id}>{x.node}</React.Fragment>)}</>;
  const current = pick != null && pick < list.length ? pick : firstOpen > -1 ? firstOpen : list.length - 1;
  const here = list[current];
  const nextOpen = list.findIndex((x, i) => i !== current && NEEDS.has(x.status));
  const handled = !NEEDS.has(here.status);
  const thing = noun || (list.every((x) => /study|launch/i.test(x.kind || "launch")) ? "studies" : "cards");
  const sum = summary(list, thing);
  const tone = toneOf(here.status);
  const go = (i) => { setPick(i); setShowAll(false); };
  return (
    <Wrap>
      <Nav role="navigation" aria-label={`${sum.head} in this answer`}>
        <div className="sum"><b>{sum.head} in this answer</b>{sum.rest ? `: ${sum.rest}` : ""}</div>
        <div className="row">
          <Arrow type="button" aria-label="Previous" disabled={current === 0} onClick={() => go(current - 1)}>{"‹"}</Arrow>
          <div className="now">
            <span className="k">{current + 1} of {list.length}</span>
            <span className="t" title={nameOf(here)}>{nameOf(here)}</span>
            <span className="s" style={{ color: TONE[tone].fg }}>{wordOf(here)}</span>
          </div>
          <Arrow type="button" aria-label="Next" disabled={current === list.length - 1} onClick={() => go(current + 1)}>{"›"}</Arrow>
        </div>
        <div className="pills">
          {list.map((x, i) => (
            <Pill key={x.id} type="button" $tone={toneOf(x.status)} $current={i === current} title={`${i + 1}. ${nameOf(x)}: ${wordOf(x)}`}
              aria-label={`${i + 1}. ${nameOf(x)}: ${wordOf(x)}`} aria-current={i === current ? "step" : undefined} onClick={() => go(i)}>
              {!NEEDS.has(x.status) && toneOf(x.status) === "green" ? "✓" : i + 1}
            </Pill>
          ))}
          <button type="button" className="all" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>{showAll ? "Hide list" : `See all ${list.length}`}</button>
        </div>
        {showAll ? (
          <List>
            {list.map((x, i) => (
              <li key={x.id}>
                <button type="button" aria-current={i === current ? "step" : undefined} onClick={() => go(i)}>
                  <span style={{ color: FIG_MUTED, width: 16, flex: "none" }}>{i + 1}</span>
                  <span className="t">{nameOf(x)}</span>
                  <span className="s" style={{ color: TONE[toneOf(x.status)].fg }}>{wordOf(x)}</span>
                </button>
              </li>
            ))}
          </List>
        ) : null}
      </Nav>
      {here.node}
      {handled && nextOpen > -1 ? (
        <NextBar>
          <span>Next to confirm: {nextOpen + 1} of {list.length}, {nameOf(list[nextOpen])}</span>
          <button type="button" onClick={() => go(nextOpen)}>Next</button>
        </NextBar>
      ) : null}
    </Wrap>
  );
}

/** The step items for task cards: what CardSteps needs from each task. */
export const taskStep = (task, node) => (task ? {
  id: task._id,
  status: task.status,
  kind: task.template === "field_new_study" ? "launch" : "task",
  title: task.study_config?.name || task.title,
  // A launch reads as launching until its study exists, then live.
  word: task.template === "field_new_study" && ["queued", "running"].includes(task.status)
    ? (task.scope?.study_id ? "Live" : "Launching")
    : task.template === "field_new_study" && task.status === "done" && !task.scope?.study_id ? "Simulated only" : null,
  node,
} : null);
